import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { addAlpha } from '@algoan/deep-dive/theme';
import { messages, categoryGroupMessages } from './cash-flow-modal-messages';
import { matchWordToSearch } from '@algoan/dashboard/utils';
import { TransactionCategory, AutocompleteInputType } from '@algoan/dashboard/model';
import { useTransactionCategoryTranslator } from '../../messages';
import { CategoryDropdownOption, CategoryGroup } from './categories-list';

interface ListItemAutocompleteProps {
  textFieldValue: string;
  handleChangeCategory(categorySelected: TransactionCategory): void;
  categoriesList: CategoryDropdownOption[];
  dropDownHeader: string;
  oldCategory?: TransactionCategory;
  currentCategory?: TransactionCategory;
}

export const ListItemAutocomplete: React.FC<ListItemAutocompleteProps> = ({
  textFieldValue,
  categoriesList,
  handleChangeCategory,
  dropDownHeader,
  oldCategory,
  currentCategory,
}) => {
  const intl = useIntl();
  const translateTransactionCategory = useTransactionCategoryTranslator();
  const listCategoriesFilter = categoriesList.filter((element) =>
    matchWordToSearch(translateTransactionCategory(element.category), textFieldValue),
  );

  return listCategoriesFilter.length > 0 ? (
    <S.DropdownList type={AutocompleteInputType.AUTOCOMPLETE}>
      {oldCategory && (
        <>
          <S.ListItemInformation>{intl.formatMessage(messages.oldCategory)}</S.ListItemInformation>
          <S.ListItem
            type={AutocompleteInputType.AUTOCOMPLETE}
            onClick={() => handleChangeCategory(oldCategory as TransactionCategory)}
          >
            <S.ListItemCategory type={AutocompleteInputType.AUTOCOMPLETE}>
              <span>{translateTransactionCategory(oldCategory)}</span>
            </S.ListItemCategory>
          </S.ListItem>
        </>
      )}
      <S.ListItemInformation>{dropDownHeader}</S.ListItemInformation>
      {listCategoriesFilter.map((element, index) => {
        return (
          <S.ListItem
            type={AutocompleteInputType.AUTOCOMPLETE}
            key={`${index}-${element.category}`}
            onClick={() => handleChangeCategory(element.category)}
          >
            <S.ListItemCategory type={AutocompleteInputType.AUTOCOMPLETE}>
              <span>{intl.formatMessage(categoryGroupMessages[element.categoryGroup as CategoryGroup])}</span>
              <S.Category isCurrent={currentCategory === element.category}>
                {translateTransactionCategory(element.category)}
              </S.Category>
            </S.ListItemCategory>
          </S.ListItem>
        );
      })}
    </S.DropdownList>
  ) : null;
};

const S = {
  DropdownList: styled.ul<{ type: AutocompleteInputType }>`
    ${(props) => props.type === AutocompleteInputType.AUTOCOMPLETE && 'max-height: 55vh; overflow-y: auto;'};
    padding: 0;
    margin-top: 8px;
    background: #ffffff;
    box-shadow: 0 1px 15px ${(props) => addAlpha(props.theme.algoanBlue, 0.15)};
    color: ${(props) => props.theme.algoanBlue};
    font-weight: 400;
    border-radius: 4px;
    font-size: 14px;

    & > li:hover > ul {
      display: block;
    }
  `,
  ListItemInformation: styled.li`
    display: flex;
    list-style: none;
    align-items: center;
    font-size: 14px;
    height: 40px;
    padding-left: 16px;
    color: ${(props) => props.theme.secondary.softBlue2};
    background: ${(props) => props.theme.secondary.chalk};
  `,
  ListItem: styled.li<{ type: AutocompleteInputType }>`
    ${(props) => (props.type === AutocompleteInputType.SELECT ? 'height: 40px' : 'min-height: 40px')};
    display: flex;
    border-bottom: 1px solid ${(props) => props.theme.iceBlue};
    list-style: none;
    align-items: center;
    font-size: 15px;
    padding-left: 24px;
    &:hover {
      background: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
      cursor: pointer;
    }

    &:last-child {
      border: none;
    }
  `,
  ListItemCategory: styled.div<{ type: AutocompleteInputType }>`
    ${(props) => (props.type === AutocompleteInputType.SELECT ? 'min-width: 309px' : 'width: 100%')};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-right: 17px;
    padding-top: 7px;
    padding-bottom: 7px;

    & > :first-child {
      max-width: ${(props) => (props.type === AutocompleteInputType.SELECT ? '50' : '47')}%;
      text-align: left;
    }

    & > :last-child {
      max-width: 100%;
      text-align: right;
    }
  `,
  Category: styled.span<{ isCurrent: boolean }>`
    font-weight: ${({ isCurrent }) => (isCurrent ? 'bold' : 'normal')};
  `,
};
